import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut, Plus, Edit, Trash2, Package, ChevronDown, ExternalLink, UserRound, Truck, RefreshCw, Upload, Download, XCircle, Palette, Copy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatINR, CATEGORIES, getCategorySlug, getProductCategories, productHasCategory } from "../lib/utils-cynos";
import { formatISTDateTime } from "../utils/dateTime";
import AdminStats from "../components/admin/AdminStats";
import {
  createProduct as adminCreateProduct,
  createDeliveryPartner,
  createNimbusPostShipment,
  cancelNimbusPostShipment,
  deleteDeliveryPartner,
  deleteProduct as adminDeleteProduct,
  getAdminDashboardData,
  listCustomDesignRequests,
  listDeliveryPartners,
  retryQikinkOrder,
  syncQikinkProducts,
  trackNimbusPostShipment,
  updateCustomDesignStatus,
  updateDeliveryPartner,
  updateOrderStatus as adminUpdateOrderStatus,
  updateProduct as adminUpdateProduct,
} from "../services/adminService";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  createCoupon,
  deleteCoupon,
  duplicateCoupon,
  listAdminCoupons,
  updateCoupon,
} from "../services/couponService";

const STATUSES = ["pending", "confirmed", "processing", "shipped", "out_for_delivery", "delivered", "cancelled", "payment_failed", "payment_pending", "returned", "refunded"];
const PRODUCT_STATUS_LABELS = { 0: "Inactive", 1: "Active", 2: "Coming Soon" };
const COUPON_STATUS_LABELS = { 0: "Inactive", 1: "Active", 2: "Scheduled" };
const CUSTOM_DESIGN_STATUSES = ["new", "reviewing", "quoted", "approved", "completed", "rejected"];
const emptyDeliveryPartner = {
  partner_name: "",
  contact_person: "",
  phone: "",
  email: "",
  tracking_url: "",
  api_key: "",
  status: 1,
};
const couponFilterOptions = ["active", "inactive", "expired", "scheduled"];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const fromDateTimeLocal = (value) => (value ? new Date(value).toISOString() : null);

const emptyCoupon = () => ({
  coupon_code: "",
  coupon_name: "",
  description: "",
  discount_type: "percentage",
  discount_value: 10,
  minimum_order_amount: 0,
  maximum_discount_amount: "",
  usage_limit: "",
  applicable_for: "all_products",
  applicable_product_ids: "",
  applicable_category_ids: "",
  start_date: toDateTimeLocal(new Date().toISOString()),
  end_date: "",
  active_status: 1,
  auto_apply: false,
  customer_limit_per_user: 1,
  first_order_only: false,
});

const AdminDashboard = () => {
  const { admin, checking, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [qikinkRetrying, setQikinkRetrying] = useState("");
  const [qikinkSyncing, setQikinkSyncing] = useState(false);
  const [qikinkSyncResult, setQikinkSyncResult] = useState(null);
  const [shippingCreating, setShippingCreating] = useState("");
  const [shippingTracking, setShippingTracking] = useState("");
  const [shippingCancelling, setShippingCancelling] = useState("");
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [customDesignRequests, setCustomDesignRequests] = useState([]);
  const [customDesignLoading, setCustomDesignLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponStatusFilter, setCouponStatusFilter] = useState("");
  const [couponSearch, setCouponSearch] = useState("");

  const refresh = useCallback(async () => {
    setError("");
    try {
      const data = await getAdminDashboardData();
      setOrders(data.orders); setProducts(data.products); setStats(data.stats);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
        return;
      }
      setError(err?.response?.data?.detail || err.message || "Unable to load admin dashboard.");
    }
  }, [logout]);

  useEffect(() => { if (admin) refresh(); }, [admin, refresh]);

  const loadDeliveryPartners = useCallback(async () => {
    setError("");
    setDeliveryLoading(true);
    try {
      setDeliveryPartners(await listDeliveryPartners());
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
        return;
      }
      setError(err?.response?.data?.detail || "Unable to load delivery partners.");
    } finally {
      setDeliveryLoading(false);
    }
  }, [logout]);

  const loadCustomDesignRequests = useCallback(async () => {
    setError("");
    setCustomDesignLoading(true);
    try {
      setCustomDesignRequests(await listCustomDesignRequests());
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
        return;
      }
      setError(err?.response?.data?.detail || "Unable to load custom design requests.");
    } finally {
      setCustomDesignLoading(false);
    }
  }, [logout]);

  const loadCoupons = useCallback(async () => {
    setError("");
    setCouponsLoading(true);
    try {
      setCoupons(await listAdminCoupons(couponStatusFilter || null, couponSearch || null));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
        return;
      }
      setError(err.message || "Unable to load coupons.");
    } finally {
      setCouponsLoading(false);
    }
  }, [couponSearch, couponStatusFilter, logout]);

  useEffect(() => {
    if (!admin) return;
    if (tab === "delivery") loadDeliveryPartners();
    if (tab === "designs") loadCustomDesignRequests();
    if (tab === "coupons") loadCoupons();
  }, [admin, tab, loadDeliveryPartners, loadCustomDesignRequests, loadCoupons]);

  if (checking) return <div className="pt-32 text-center font-mono text-zinc-500 uppercase">Authenticating...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const updateOrderStatus = async (id, status) => {
    const reason = status === "cancelled" ? window.prompt("Cancellation reason (optional)") || "" : "";
    await adminUpdateOrderStatus(id, status, reason);
    refresh();
  };

  const retryQikink = async (id) => {
    setError("");
    setQikinkRetrying(id);
    try {
      await retryQikinkOrder(id);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not retry Qikink push");
    } finally {
      setQikinkRetrying("");
    }
  };

  const syncQikink = async () => {
    setError("");
    setQikinkSyncResult(null);
    setQikinkSyncing(true);
    try {
      const result = await syncQikinkProducts();
      setQikinkSyncResult(result);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not sync Qikink products");
    } finally {
      setQikinkSyncing(false);
    }
  };

  const createShipping = async (id) => {
    if (!window.confirm("Create NimbusPost shipment for this order?")) return;
    setError("");
    setShippingCreating(id);
    try {
      await createNimbusPostShipment(id);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create NimbusPost shipment");
    } finally {
      setShippingCreating("");
    }
  };

  const trackShipping = async (id) => {
    setError("");
    setShippingTracking(id);
    try {
      await trackNimbusPostShipment(id);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not refresh NimbusPost tracking");
    } finally {
      setShippingTracking("");
    }
  };

  const cancelShipping = async (id) => {
    if (!window.confirm("Cancel this NimbusPost shipment?")) return;
    setError("");
    setShippingCancelling(id);
    try {
      await cancelNimbusPostShipment(id);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not cancel NimbusPost shipment");
    } finally {
      setShippingCancelling("");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await adminDeleteProduct(id);
    refresh();
  };

  const saveDeliveryPartner = async (partner) => {
    setError("");
    try {
      if (partner.id) await updateDeliveryPartner(partner.id, partner);
      else await createDeliveryPartner(partner);
      await loadDeliveryPartners();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save delivery partner.");
      throw err;
    }
  };

  const removeDeliveryPartner = async (id) => {
    if (!window.confirm("Delete this delivery partner?")) return;
    setError("");
    try {
      await deleteDeliveryPartner(id);
      await loadDeliveryPartners();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not delete delivery partner.");
    }
  };

  const changeCustomDesignStatus = async (id, status) => {
    setError("");
    try {
      await updateCustomDesignStatus(id, status);
      await loadCustomDesignRequests();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not update custom design request.");
    }
  };

  const saveCoupon = async (coupon) => {
    setError("");
    try {
      if (coupon.id) await updateCoupon(coupon.id, coupon);
      else await createCoupon(coupon);
      await loadCoupons();
    } catch (err) {
      setError(err.message || "Could not save coupon.");
      throw err;
    }
  };

  const removeCouponCode = async (id) => {
    if (!window.confirm("Delete this coupon? Existing orders keep their coupon snapshot.")) return;
    setError("");
    try {
      await deleteCoupon(id);
      await loadCoupons();
    } catch (err) {
      setError(err.message || "Could not delete coupon.");
    }
  };

  const cloneCoupon = async (id) => {
    setError("");
    try {
      await duplicateCoupon(id);
      await loadCoupons();
    } catch (err) {
      setError(err.message || "Could not duplicate coupon.");
    }
  };

  return (
    <div className="min-h-screen bg-cynos-bg" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-6">
          <Link to="/" className="font-anton text-2xl uppercase">CYNOS<span className="text-cynos-red">.</span></Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-mono text-xs hidden md:inline">{admin.email}</span>
          <button onClick={logout} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] hover:text-cynos-red" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="px-4 md:px-8 py-8">
        {error && (
          <div className="mb-6 border border-cynos-red/40 bg-cynos-red/10 p-4 font-mono text-xs uppercase tracking-[0.18em] text-red-200">
            {error}
          </div>
        )}

        <AdminStats stats={stats} />

        <div className="flex flex-wrap gap-2 mb-6">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders">Orders</TabBtn>
          <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="tab-products">Products</TabBtn>
          <TabBtn active={tab === "coupons"} onClick={() => setTab("coupons")} testid="tab-coupons">Coupons</TabBtn>
          <TabBtn active={tab === "delivery"} onClick={() => setTab("delivery")} testid="tab-delivery">Delivery</TabBtn>
          <TabBtn active={tab === "designs"} onClick={() => setTab("designs")} testid="tab-designs">Designs</TabBtn>
          <Link to="/admin/blogs" className="px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-white/15 hover:border-white" data-testid="tab-blogs">Blogs</Link>
        </div>

        {tab === "orders" ? (
          <OrdersTable
            orders={orders}
            onStatusChange={updateOrderStatus}
            onQikinkRetry={retryQikink}
            qikinkRetrying={qikinkRetrying}
            onShippingCreate={createShipping}
            onShippingTrack={trackShipping}
            onShippingCancel={cancelShipping}
            shippingCreating={shippingCreating}
            shippingTracking={shippingTracking}
            shippingCancelling={shippingCancelling}
          />
        ) : tab === "products" ? (
          <ProductsTable products={products} onDelete={deleteProduct} onSaved={refresh} onQikinkSync={syncQikink} qikinkSyncing={qikinkSyncing} qikinkSyncResult={qikinkSyncResult} />
        ) : tab === "coupons" ? (
          <CouponsPanel
            coupons={coupons}
            loading={couponsLoading}
            statusFilter={couponStatusFilter}
            search={couponSearch}
            onStatusFilterChange={setCouponStatusFilter}
            onSearchChange={setCouponSearch}
            onRefresh={loadCoupons}
            onSave={saveCoupon}
            onDelete={removeCouponCode}
            onDuplicate={cloneCoupon}
          />
        ) : tab === "delivery" ? (
          <DeliveryPartnersPanel partners={deliveryPartners} loading={deliveryLoading} onSave={saveDeliveryPartner} onDelete={removeDeliveryPartner} />
        ) : (
          <CustomDesignRequestsPanel requests={customDesignRequests} loading={customDesignLoading} onStatusChange={changeCustomDesignStatus} />
        )}
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} className={`px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] border ${active ? "bg-white text-black border-white" : "border-white/15 hover:border-white"}`} data-testid={testid}>
    {children}
  </button>
);

const OrdersTable = ({
  orders,
  onStatusChange,
  onQikinkRetry,
  qikinkRetrying,
  onShippingCreate,
  onShippingTrack,
  onShippingCancel,
  shippingCreating,
  shippingTracking,
  shippingCancelling,
}) => {
  const [expanded, setExpanded] = useState(null);
  const [detailPanel, setDetailPanel] = useState("items");

  const toggleDetail = (orderId, panel) => {
    if (expanded === orderId && detailPanel === panel) {
      setExpanded(null);
      return;
    }
    setExpanded(orderId);
    setDetailPanel(panel);
  };

  return (
    <div className="bg-cynos-card border border-white/10 overflow-x-auto" data-testid="orders-table">
      <table className="w-full text-sm">
        <thead className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 border-b border-white/10">
          <tr>
            <th className="text-left p-4">Order</th>
            <th className="text-left p-4">Customer</th>
            <th className="text-left p-4">Items</th>
            <th className="text-left p-4">Total</th>
            <th className="text-left p-4">Payment</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Details</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500 font-mono text-xs uppercase tracking-[0.2em]">No orders yet</td></tr>}
          {orders.map((o) => (
            <React.Fragment key={o.id}>
              <tr className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="p-4 font-mono text-xs uppercase">{o.id.slice(0, 8)}</td>
                <td className="p-4">
                  <div className="font-bold">{o.customer.full_name}</div>
                  <div className="text-xs text-zinc-400">{o.customer.city}, {o.customer.state}</div>
                </td>
                <td className="p-4">{o.items.length}</td>
                <td className="p-4 font-anton text-lg">{formatINR(o.total)}</td>
                <td className="p-4 font-mono text-xs uppercase">
                  <div>{o.payment_method}</div>
                  <div className="text-[10px] text-zinc-500">{(o.payment_status || "not_required").replaceAll("_", " ")}</div>
                </td>
                <td className="p-4">
                  <Select value={o.status} onValueChange={(v) => onStatusChange(o.id, v)}>
                    <SelectTrigger className="w-[180px] bg-cynos-surface border-white/15 rounded-none" data-testid={`status-${o.id.slice(0,8)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-cynos-card border-white/15 rounded-none">
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="uppercase font-mono text-xs">{s.replaceAll("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-2">
                    <DetailToggle
                      active={expanded === o.id && detailPanel === "items"}
                      icon={Package}
                      onClick={() => toggleDetail(o.id, "items")}
                    >
                      Items
                    </DetailToggle>
                    <DetailToggle
                      active={expanded === o.id && detailPanel === "customer"}
                      icon={UserRound}
                      onClick={() => toggleDetail(o.id, "customer")}
                    >
                      Customer
                    </DetailToggle>
                    <DetailToggle
                      active={expanded === o.id && detailPanel === "qikink"}
                      icon={Truck}
                      onClick={() => toggleDetail(o.id, "qikink")}
                    >
                      Qikink
                    </DetailToggle>
                    <DetailToggle
                      active={expanded === o.id && detailPanel === "shipping"}
                      icon={Truck}
                      onClick={() => toggleDetail(o.id, "shipping")}
                    >
                      Shipping
                    </DetailToggle>
                  </div>
                </td>
              </tr>
              {expanded === o.id && (
                <tr className="border-b border-white/10 bg-black/30">
                  <td colSpan={7} className="p-4">
                    <div className="grid gap-3">
                      {detailPanel === "items" ? (
                        o.items.map((item, index) => (
                          <OrderItemDetail key={`${o.id}-${item.product_id}-${index}`} item={item} />
                        ))
                      ) : detailPanel === "customer" ? (
                        <OrderCustomerDetail order={o} />
                      ) : detailPanel === "qikink" ? (
                        <OrderQikinkDetail order={o} onRetry={onQikinkRetry} retrying={qikinkRetrying === o.id} />
                      ) : (
                        <OrderShippingDetail
                          order={o}
                          onCreate={onShippingCreate}
                          onTrack={onShippingTrack}
                          onCancel={onShippingCancel}
                          creating={shippingCreating === o.id}
                          tracking={shippingTracking === o.id}
                          cancelling={shippingCancelling === o.id}
                        />
                      )}
                      {o.status_history?.length > 0 && (
                        <div className="mt-2 border-t border-white/10 pt-4">
                          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Status History</div>
                          <div className="flex flex-wrap gap-2">
                            {o.status_history.map((entry, index) => (
                              <span key={`${entry.status}-${index}`} className="border border-white/10 bg-cynos-surface px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300">
                                {entry.status.replaceAll("_", " ")} - {formatISTDateTime(entry.timestamp)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DetailToggle = ({ active, icon: Icon, onClick, children }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
      active ? "border-white bg-white text-black" : "border-white/20 hover:border-white"
    }`}
  >
    {active ? <ChevronDown size={14} className="rotate-180 transition-transform" /> : <Icon size={14} />}
    {children}
  </button>
);

const OrderItemDetail = ({ item }) => (
  <div className="grid gap-4 border border-white/10 bg-cynos-surface p-4 md:grid-cols-[72px_1fr_auto]">
    <div>{item.image && <img src={item.image} alt={item.name} className="h-20 w-16 object-cover" />}</div>
    <div className="min-w-0">
      <div className="font-bold uppercase">{item.name}</div>
      <div className="mt-2 grid gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 sm:grid-cols-2 lg:grid-cols-3">
        <span>Product ID: {item.product_id}</span>
        <span>SKU: {item.sku || "N/A"}</span>
        <span>Slug: {item.slug || "N/A"}</span>
        <span>Size: {item.size}</span>
        <span>Color: {item.color || "N/A"}</span>
        <span>Qty: {item.qty}</span>
        <span>Price: {formatINR(item.price)}</span>
      </div>
      {item.variant_details && Object.keys(item.variant_details).length > 0 && (
        <pre className="mt-3 max-h-28 overflow-auto border border-white/10 bg-black/40 p-3 text-[10px] text-zinc-400">
          {JSON.stringify(item.variant_details, null, 2)}
        </pre>
      )}
    </div>
    <div className="flex items-start">
      {item.slug ? (
        <Link to={`/product/${item.slug}`} className="inline-flex items-center gap-2 bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-black">
          View Product <ExternalLink size={12} />
        </Link>
      ) : (
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">No URL</span>
      )}
    </div>
  </div>
);

const OrderQikinkDetail = ({ order, onRetry, retrying }) => {
  const status = order.qikink_status || "not_attempted";
  const isOk = status === "ok";
  const isError = status === "error";

  return (
    <div className="grid gap-4 border border-white/10 bg-cynos-surface p-4 lg:grid-cols-[1fr_auto]">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <QikinkInfo label="Push Status" value={status.replaceAll("_", " ")} tone={isOk ? "text-emerald-300" : isError ? "text-red-300" : "text-zinc-300"} />
        <QikinkInfo label="Order Number" value={order.qikink_order_number || "N/A"} />
        <QikinkInfo label="Qikink Order ID" value={order.qikink_order_id || "N/A"} />
        <QikinkInfo label="Attempted" value={formatISTDateTime(order.qikink_attempted_at)} />
        {order.qikink_error && (
          <div className="sm:col-span-2 lg:col-span-4 border border-red-500/20 bg-red-500/10 p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-red-200">
            {order.qikink_error}
          </div>
        )}
      </div>
      <div className="flex items-start justify-end">
        <button
          onClick={() => onRetry(order.id)}
          disabled={retrying}
          className="inline-flex items-center gap-2 border border-white/20 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition hover:border-white disabled:opacity-50"
        >
          <RefreshCw size={14} className={retrying ? "animate-spin" : ""} />
          {retrying ? "Retrying" : "Retry Push"}
        </button>
      </div>
    </div>
  );
};

const OrderShippingDetail = ({ order, onCreate, onTrack, onCancel, creating, tracking, cancelling }) => {
  const status = order.shipping_status || "not_created";
  const hasAwb = Boolean(order.awb_number);
  const isError = status === "error" || Boolean(order.shipping_error);
  const isCancelled = status === "cancelled" || order.tracking_status === "cancelled";
  const needsNimbusVerification = /otp|support email|shipping label/i.test(order.shipping_error || "");

  return (
    <div className="grid gap-4 border border-white/10 bg-cynos-surface p-4 lg:grid-cols-[1fr_auto]">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <QikinkInfo label="Shipping Partner" value={order.shipping_partner || "NimbusPost"} />
        <QikinkInfo label="Shipping Status" value={status.replaceAll("_", " ")} tone={isError ? "text-red-300" : status === "not_created" ? "text-zinc-500" : "text-emerald-300"} />
        <QikinkInfo label="AWB Number" value={order.awb_number || "N/A"} />
        <QikinkInfo label="Courier" value={order.courier_name || order.courier_id || "N/A"} />
        <QikinkInfo label="Shipment ID" value={order.shipment_id || "N/A"} />
        <QikinkInfo label="Nimbus Order ID" value={order.nimbuspost_order_id || "N/A"} />
        <QikinkInfo label="Tracking Status" value={(order.tracking_status || "N/A").replaceAll("_", " ")} />
        <QikinkInfo label="Updated" value={formatISTDateTime(order.shipping_updated_at)} />
        {order.tracking_url && (
          <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-200 hover:border-white">
            Tracking URL <ExternalLink size={12} />
          </a>
        )}
        {order.label_url && (
          <a href={order.label_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-200 hover:border-white">
            <Download size={12} /> Label
          </a>
        )}
        {order.shipping_error && (
          <div className="sm:col-span-2 lg:col-span-4 border border-red-500/20 bg-red-500/10 p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-red-200">
            {order.shipping_error}
          </div>
        )}
        {needsNimbusVerification && (
          <div className="sm:col-span-2 lg:col-span-4 border border-amber-400/20 bg-amber-400/10 p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-amber-100">
            Fix in NimbusPost: verify the support email and phone used on shipping labels, then retry Create Shipment.
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-start justify-end gap-2">
        {!hasAwb && (
          <button
            onClick={() => onCreate(order.id)}
            disabled={creating}
            className="inline-flex items-center gap-2 border border-white/20 bg-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-black transition hover:bg-zinc-200 disabled:opacity-50"
          >
            <Truck size={14} />
            {creating ? "Creating" : "Create Shipment"}
          </button>
        )}
        <button
          onClick={() => onTrack(order.id)}
          disabled={!hasAwb || tracking}
          className="inline-flex items-center gap-2 border border-white/20 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition hover:border-white disabled:opacity-50"
        >
          <RefreshCw size={14} className={tracking ? "animate-spin" : ""} />
          {tracking ? "Refreshing" : "Refresh Tracking"}
        </button>
        <button
          onClick={() => onCancel(order.id)}
          disabled={!hasAwb || cancelling || isCancelled}
          className="inline-flex items-center gap-2 border border-cynos-red/40 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-red-200 transition hover:border-cynos-red disabled:opacity-50"
        >
          <XCircle size={14} />
          {cancelling ? "Cancelling" : "Cancel Shipment"}
        </button>
      </div>
    </div>
  );
};

const QikinkInfo = ({ label, value, tone = "text-zinc-300" }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
    <div className={`mt-2 break-words font-mono text-xs uppercase tracking-[0.12em] ${tone}`}>{value}</div>
  </div>
);

const OrderCustomerDetail = ({ order }) => {
  const profile = order.customer_profile || {};
  const snapshot = order.customer || {};
  const shipping = order.shipping_address || profile.shipping_address || snapshot.shipping_address || snapshot;
  const billing = order.billing_address || profile.billing_address || snapshot.billing_address;
  const name = profile.full_name || profile.name || snapshot.full_name || "Customer";
  const email = profile.email || snapshot.email || "N/A";
  const phone = profile.phone || snapshot.phone || shipping?.phone || "N/A";

  return (
    <div className="grid gap-4 border border-white/10 bg-cynos-surface p-4 lg:grid-cols-[260px_1fr_1fr]">
      <div className="flex gap-4">
        {profile.picture ? (
          <img src={profile.picture} alt={name} className="h-16 w-16 border border-white/10 object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center border border-white/10 bg-black/40">
            <UserRound size={24} className="text-zinc-500" />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-bold uppercase">{name}</div>
          <div className="mt-2 grid gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">
            <span>Email: {email}</span>
            <span>Mobile: {phone}</span>
            <span>Customer ID: {order.customer_id || "Guest checkout"}</span>
            <span>Login: {profile.auth_provider || "Checkout snapshot"}</span>
          </div>
        </div>
      </div>

      <AddressBlock title="Shipping Address" address={shipping} fallbackName={name} fallbackPhone={phone} />
      <AddressBlock title="Billing Address" address={billing} fallbackName={name} fallbackPhone={phone} emptyText="Same as shipping" />
    </div>
  );
};

const AddressBlock = ({ title, address, fallbackName, fallbackPhone, emptyText = "N/A" }) => {
  if (!address) {
    return (
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{title}</div>
        <div className="mt-3 text-sm text-zinc-400">{emptyText}</div>
      </div>
    );
  }

  const lines = [
    address.full_name || fallbackName,
    address.phone || fallbackPhone,
    address.address_line1,
    address.address_line2,
    [address.city, address.state, address.pincode].filter(Boolean).join(", "),
    address.country,
  ].filter(Boolean);

  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{title}</div>
      <div className="mt-3 space-y-1 text-sm text-zinc-300">
        {lines.length ? lines.map((line, index) => <div key={`${title}-${index}`}>{line}</div>) : <div>{emptyText}</div>}
      </div>
    </div>
  );
};

const publicAssetUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
};

const CouponsPanel = ({
  coupons,
  loading,
  statusFilter,
  search,
  onStatusFilterChange,
  onSearchChange,
  onRefresh,
  onSave,
  onDelete,
  onDuplicate,
}) => {
  const [editing, setEditing] = useState(null);

  return (
    <div className="space-y-4" data-testid="coupons-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-anton text-3xl uppercase">Coupons</h2>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            Manage discount codes, scheduling, product/category rules, and customer limits.
          </p>
        </div>
        <button
          onClick={() => setEditing(emptyCoupon())}
          className="inline-flex items-center gap-2 bg-cynos-red px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-red-600"
          data-testid="add-coupon-btn"
        >
          <Plus size={14} /> Add Coupon
        </button>
      </div>

      <div className="grid gap-3 border border-white/10 bg-cynos-card p-4 md:grid-cols-[220px_1fr_auto]">
        <select
          value={statusFilter}
          onChange={(event) => onStatusFilterChange(event.target.value)}
          className="bg-cynos-surface border border-white/15 px-3 py-3 font-mono text-xs uppercase tracking-[0.16em] outline-none focus:border-white"
          data-testid="coupon-status-filter"
        >
          <option value="">All Status</option>
          {couponFilterOptions.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search coupon code or name"
          className="bg-cynos-surface border border-white/15 px-3 py-3 font-mono text-xs uppercase tracking-[0.16em] outline-none focus:border-white"
          data-testid="coupon-search"
        />
        <button
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:border-white disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="bg-cynos-card border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 border-b border-white/10">
            <tr>
              <th className="text-left p-4">Code</th>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Discount</th>
              <th className="text-left p-4">Min Order</th>
              <th className="text-left p-4">Usage</th>
              <th className="text-left p-4">Valid</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Loading coupons</td></tr>}
            {!loading && coupons.length === 0 && <tr><td colSpan={8} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">No coupons found</td></tr>}
            {coupons.map((coupon) => {
              const expired = coupon.end_date && new Date(coupon.end_date) < new Date();
              return (
                <tr key={coupon.id} className="border-b border-white/5 align-top">
                  <td className="p-4 font-mono text-xs uppercase tracking-[0.16em] text-white">{coupon.coupon_code}</td>
                  <td className="p-4">
                    <div className="font-bold uppercase">{coupon.coupon_name}</div>
                    {coupon.description && <div className="mt-1 max-w-xs text-xs text-zinc-500">{coupon.description}</div>}
                  </td>
                  <td className="p-4 font-mono text-xs uppercase">
                    {coupon.discount_type === "percentage" ? `${coupon.discount_value}%` : formatINR(coupon.discount_value)}
                    {coupon.maximum_discount_amount ? <div className="mt-1 text-[10px] text-zinc-500">Cap {formatINR(coupon.maximum_discount_amount)}</div> : null}
                  </td>
                  <td className="p-4 font-mono text-xs">{formatINR(coupon.minimum_order_amount || 0)}</td>
                  <td className="p-4 font-mono text-xs">
                    {coupon.used_count || 0}/{coupon.usage_limit || "Unlimited"}
                    <div className="mt-1 text-[10px] uppercase text-zinc-500">Per user {coupon.customer_limit_per_user || 1}</div>
                  </td>
                  <td className="p-4 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                    <div>Start: {formatISTDateTime(coupon.start_date)}</div>
                    <div className="mt-1">End: {formatISTDateTime(coupon.end_date)}</div>
                  </td>
                  <td className={`p-4 font-mono text-[10px] uppercase tracking-[0.16em] ${expired ? "text-red-300" : Number(coupon.active_status) === 1 ? "text-emerald-300" : Number(coupon.active_status) === 2 ? "text-cynos-red" : "text-zinc-500"}`}>
                    {expired ? "Expired" : COUPON_STATUS_LABELS[Number(coupon.active_status ?? 1)] || "Active"}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(coupon)} className="border border-white/20 p-2 hover:border-white" data-testid={`edit-coupon-${coupon.coupon_code}`}><Edit size={14} /></button>
                      <button onClick={() => onDuplicate(coupon.id)} className="border border-white/20 p-2 hover:border-white" data-testid={`duplicate-coupon-${coupon.coupon_code}`}><Copy size={14} /></button>
                      <button onClick={() => onDelete(coupon.id)} className="border border-cynos-red/40 p-2 text-cynos-red hover:border-cynos-red" data-testid={`delete-coupon-${coupon.coupon_code}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <CouponDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            await onSave(payload);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const CouponDialog = ({ initial, onClose, onSave }) => {
  const isEdit = !!initial.id;
  const normalizeList = (value) => Array.isArray(value) ? value.join(", ") : (value || "");
  const [data, setData] = useState({
    ...emptyCoupon(),
    ...initial,
    maximum_discount_amount: initial.maximum_discount_amount ?? "",
    usage_limit: initial.usage_limit ?? "",
    applicable_product_ids: normalizeList(initial.applicable_product_ids),
    applicable_category_ids: normalizeList(initial.applicable_category_ids),
    start_date: toDateTimeLocal(initial.start_date || new Date().toISOString()),
    end_date: toDateTimeLocal(initial.end_date),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (key, value) => setData((current) => ({ ...current, [key]: value }));

  const numberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
  const numberOrZero = (value) => Number(value || 0);

  const save = async () => {
    setError("");
    if (!data.coupon_code.trim() || !data.coupon_name.trim()) {
      setError("Coupon code and name are required.");
      return;
    }
    if (!data.start_date) {
      setError("Start date is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...data,
        coupon_code: data.coupon_code.trim().toUpperCase(),
        discount_value: numberOrZero(data.discount_value),
        minimum_order_amount: numberOrZero(data.minimum_order_amount),
        maximum_discount_amount: numberOrNull(data.maximum_discount_amount),
        usage_limit: data.usage_limit === "" ? null : Number(data.usage_limit),
        applicable_product_ids: splitCsv(String(data.applicable_product_ids || "")),
        applicable_category_ids: splitCsv(String(data.applicable_category_ids || "")),
        start_date: fromDateTimeLocal(data.start_date),
        end_date: fromDateTimeLocal(data.end_date),
        active_status: Number(data.active_status ?? 1),
        customer_limit_per_user: Number(data.customer_limit_per_user || 1),
        auto_apply: !!data.auto_apply,
        first_order_only: !!data.first_order_only,
      };
      if (payload.applicable_for === "all_products") {
        payload.applicable_product_ids = [];
        payload.applicable_category_ids = [];
      }
      if (isEdit) delete payload.coupon_code;
      await onSave(payload);
    } catch (err) {
      setError(err.message || "Could not save coupon.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-cynos-card border-white/10 rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-anton text-3xl uppercase">{isEdit ? "Edit" : "Add"} Coupon</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-3 md:grid-cols-2">
            <Input label="Coupon Code" value={data.coupon_code} onChange={(value) => update("coupon_code", value.toUpperCase())} testid="coupon-code" />
            <Input label="Coupon Name" value={data.coupon_name} onChange={(value) => update("coupon_name", value)} testid="coupon-name" />
          </div>
          <Textarea label="Description" value={data.description || ""} onChange={(value) => update("description", value)} testid="coupon-description" />

          <div className="grid gap-3 md:grid-cols-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Type</span>
              <select value={data.discount_type} onChange={(event) => update("discount_type", event.target.value)} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white" data-testid="coupon-type">
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed</option>
              </select>
            </label>
            <Input label="Discount Value" type="number" value={data.discount_value} onChange={(value) => update("discount_value", value)} testid="coupon-value" />
            <Input label="Min Order" type="number" value={data.minimum_order_amount} onChange={(value) => update("minimum_order_amount", value)} testid="coupon-min" />
            <Input label="Max Discount" type="number" value={data.maximum_discount_amount} onChange={(value) => update("maximum_discount_amount", value)} testid="coupon-max" />
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <Input label="Usage Limit" type="number" value={data.usage_limit} onChange={(value) => update("usage_limit", value)} testid="coupon-usage-limit" />
            <Input label="Customer Limit" type="number" value={data.customer_limit_per_user} onChange={(value) => update("customer_limit_per_user", value)} testid="coupon-customer-limit" />
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Status</span>
              <select value={Number(data.active_status ?? 1)} onChange={(event) => update("active_status", Number(event.target.value))} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white" data-testid="coupon-status">
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
                <option value={2}>Scheduled</option>
              </select>
            </label>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Input label="Start Date" type="datetime-local" value={data.start_date} onChange={(value) => update("start_date", value)} testid="coupon-start" />
            <Input label="End Date" type="datetime-local" value={data.end_date} onChange={(value) => update("end_date", value)} testid="coupon-end" />
          </div>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Applicable For</span>
            <select value={data.applicable_for} onChange={(event) => update("applicable_for", event.target.value)} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white" data-testid="coupon-applicable-for">
              <option value="all_products">All Products</option>
              <option value="specific_products">Specific Products</option>
              <option value="specific_categories">Specific Categories</option>
            </select>
          </label>
          {data.applicable_for === "specific_products" && (
            <Input label="Product IDs (comma-separated)" value={data.applicable_product_ids} onChange={(value) => update("applicable_product_ids", value)} testid="coupon-product-ids" />
          )}
          {data.applicable_for === "specific_categories" && (
            <Input label="Category IDs / Slugs (comma-separated)" value={data.applicable_category_ids} onChange={(value) => update("applicable_category_ids", value)} testid="coupon-category-ids" />
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex items-center gap-3 border border-white/10 bg-cynos-surface p-3">
              <input type="checkbox" checked={!!data.auto_apply} onChange={(event) => update("auto_apply", event.target.checked)} className="accent-cynos-red" />
              <span className="font-mono text-xs uppercase tracking-[0.2em]">Auto Apply</span>
            </label>
            <label className="flex items-center gap-3 border border-white/10 bg-cynos-surface p-3">
              <input type="checkbox" checked={!!data.first_order_only} onChange={(event) => update("first_order_only", event.target.checked)} className="accent-cynos-red" />
              <span className="font-mono text-xs uppercase tracking-[0.2em]">First Order Only</span>
            </label>
          </div>

          {error && <div className="border border-cynos-red/40 bg-cynos-red/10 p-3 font-mono text-[10px] uppercase tracking-[0.16em] text-red-200">{error}</div>}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em]">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-cynos-red px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-red-600 disabled:opacity-50" data-testid="coupon-save">
            {saving ? "Saving..." : "Save Coupon"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeliveryPartnersPanel = ({ partners, loading, onSave, onDelete }) => {
  const [editing, setEditing] = useState(null);

  return (
    <div className="space-y-4" data-testid="delivery-partners-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-anton text-3xl uppercase">Delivery Partners</h2>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Manage courier partners, contacts, and tracking links.</p>
        </div>
        <button
          onClick={() => setEditing(emptyDeliveryPartner)}
          className="inline-flex items-center gap-2 bg-cynos-red px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-red-600"
          data-testid="add-delivery-partner"
        >
          <Plus size={14} /> Add Partner
        </button>
      </div>
      <div className="bg-cynos-card border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 border-b border-white/10">
            <tr>
              <th className="text-left p-4">Partner</th>
              <th className="text-left p-4">Contact</th>
              <th className="text-left p-4">Phone</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Tracking</th>
              <th className="text-left p-4">API</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Loading partners</td></tr>}
            {!loading && partners.length === 0 && <tr><td colSpan={8} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">No delivery partners yet</td></tr>}
            {partners.map((partner) => (
              <tr key={partner.id} className="border-b border-white/5">
                <td className="p-4 font-bold uppercase">{partner.partner_name}</td>
                <td className="p-4 text-zinc-300">{partner.contact_person || "N/A"}</td>
                <td className="p-4 font-mono text-xs">{partner.phone || "N/A"}</td>
                <td className="p-4 text-zinc-300">{partner.email || "N/A"}</td>
                <td className="p-4">
                  {partner.tracking_url ? (
                    <a href={partner.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-cynos-red">
                      Open <ExternalLink size={12} />
                    </a>
                  ) : "N/A"}
                </td>
                <td className="p-4 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">{partner.api_key ? "Configured" : "N/A"}</td>
                <td className="p-4 font-mono text-[10px] uppercase tracking-[0.16em]">{Number(partner.status) === 1 ? "Active" : "Inactive"}</td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(partner)} className="border border-white/20 p-2 hover:border-white" data-testid={`edit-partner-${partner.id}`}><Edit size={14} /></button>
                    <button onClick={() => onDelete(partner.id)} className="border border-cynos-red/40 p-2 text-cynos-red hover:border-cynos-red" data-testid={`delete-partner-${partner.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <DeliveryPartnerDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            await onSave(payload);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const DeliveryPartnerDialog = ({ initial, onClose, onSave }) => {
  const [data, setData] = useState({ ...emptyDeliveryPartner, ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (key, value) => setData((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setError("");
    if (!data.partner_name.trim()) {
      setError("Partner name is required.");
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...data, status: Number(data.status ?? 1) });
    } catch {
      setError("Could not save partner.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-cynos-card border-white/10 rounded-none max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-anton text-3xl uppercase">{data.id ? "Edit" : "Add"} Delivery Partner</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Input label="Partner Name" value={data.partner_name} onChange={(value) => update("partner_name", value)} testid="partner-name" />
          <Input label="Contact Person" value={data.contact_person || ""} onChange={(value) => update("contact_person", value)} testid="partner-contact" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Phone" value={data.phone || ""} onChange={(value) => update("phone", value)} testid="partner-phone" />
            <Input label="Email" value={data.email || ""} onChange={(value) => update("email", value)} testid="partner-email" />
          </div>
          <Input label="Tracking URL" value={data.tracking_url || ""} onChange={(value) => update("tracking_url", value)} testid="partner-tracking" />
          <Input label="API Key / Credentials" value={data.api_key || ""} onChange={(value) => update("api_key", value)} testid="partner-api-key" />
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Status</span>
            <select value={Number(data.status ?? 1)} onChange={(event) => update("status", Number(event.target.value))} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white" data-testid="partner-status">
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </label>
          {error && <div className="border border-cynos-red/40 bg-cynos-red/10 p-3 font-mono text-[10px] uppercase tracking-[0.16em] text-red-200">{error}</div>}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em]">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-cynos-red px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-red-600 disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CustomDesignRequestsPanel = ({ requests, loading, onStatusChange }) => (
  <div className="space-y-4" data-testid="custom-design-admin-panel">
    <div>
      <h2 className="font-anton text-3xl uppercase">Custom Design Requests</h2>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Review customer design briefs and update their progress.</p>
    </div>
    <div className="bg-cynos-card border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 border-b border-white/10">
          <tr>
            <th className="text-left p-4">Customer</th>
            <th className="text-left p-4">Product</th>
            <th className="text-left p-4">Brief</th>
            <th className="text-left p-4">Budget</th>
            <th className="text-left p-4">Attachment</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Created</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={7} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Loading requests</td></tr>}
          {!loading && requests.length === 0 && <tr><td colSpan={7} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">No custom design requests yet</td></tr>}
          {requests.map((request) => (
            <tr key={request.id} className="border-b border-white/5 align-top">
              <td className="p-4">
                <div className="font-bold uppercase">{request.customer_name}</div>
                <div className="mt-2 grid gap-1 text-xs text-zinc-400">
                  <span>{request.email}</span>
                  <span>{request.phone}</span>
                </div>
              </td>
              <td className="p-4">
                <div className="font-mono text-xs uppercase">{request.product_type}</div>
                <div className="mt-2 text-xs text-zinc-400">Size: {request.size || "N/A"}</div>
              </td>
              <td className="p-4 max-w-md text-zinc-300">{request.design_description}</td>
              <td className="p-4 font-mono text-xs">{request.budget || "N/A"}</td>
              <td className="p-4">
                {request.attachment_url ? (
                  <a href={publicAssetUrl(request.attachment_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-cynos-red">
                    Open <ExternalLink size={12} />
                  </a>
                ) : "N/A"}
              </td>
              <td className="p-4">
                <select
                  value={request.status || "new"}
                  onChange={(event) => onStatusChange(request.id, event.target.value)}
                  className="bg-cynos-surface border border-white/15 px-3 py-2 font-mono text-xs uppercase outline-none focus:border-white"
                  data-testid={`custom-design-status-${request.id}`}
                >
                  {CUSTOM_DESIGN_STATUSES.map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}
                </select>
              </td>
              <td className="p-4 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                {formatISTDateTime(request.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const emptyProduct = {
  name: "", description: "", price: 599, original_price: 999,
  category: "men", categories: ["Men"], subCategory: "", images: [""], sizes: ["S","M","L","XL","XXL"], colors: [], color_images: {}, badges: [], stock_count: 25,
  variant_skus: {}, is_bestseller: false, rating: 4.7, review_count: 0,
  variants: [], status: "active", active_status: 1,
  meta_title: "", meta_description: "", meta_keywords: "", seo_slug: "",
  isQikinkProduct: false,
};

const splitCsv = (value) => value.split(",").map((s) => s.trim()).filter(Boolean);
const splitLines = (value) => value.split("\n").map((s) => s.trim()).filter(Boolean);
const slugPreview = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const seoTitlePreview = (data) => data.meta_title || `${data.name || "Product"} | Cynos`;
const seoDescriptionPreview = (data) => data.meta_description || data.description || "Premium Cynos streetwear product with secure checkout and fast delivery.";

const ProductsTable = ({ products, onDelete, onSaved, onQikinkSync, qikinkSyncing, qikinkSyncResult }) => {
  const [editing, setEditing] = useState(null); // null | {} | product
  const [categoryFilter, setCategoryFilter] = useState("all");
  const filteredProducts = categoryFilter === "all" ? products : products.filter((product) => productHasCategory(product, categoryFilter));

  return (
    <div className="space-y-4" data-testid="products-table">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {qikinkSyncResult && (
            <div className="border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-200">
              Qikink sync: {qikinkSyncResult.products_synced} products, {qikinkSyncResult.variations_synced} variations
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/products/import"
            className="border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:border-white"
            data-testid="import-products-page-btn"
          >
            <Upload size={14} /> Import CSV
          </Link>
          <button
            onClick={onQikinkSync}
            disabled={qikinkSyncing}
            className="border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:border-white disabled:opacity-50"
            data-testid="sync-qikink-products-btn"
          >
            <RefreshCw size={14} className={qikinkSyncing ? "animate-spin" : ""} />
            {qikinkSyncing ? "Syncing" : "Sync Qikink Products"}
          </button>
        <button
          onClick={() => setEditing(emptyProduct)}
          className="bg-cynos-red text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-red-600"
          data-testid="add-product-btn"
        >
          <Plus size={14} /> Add Product
        </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-white/10 bg-cynos-card p-3">
        <button
          type="button"
          onClick={() => setCategoryFilter("all")}
          className={`rounded-full border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] transition ${categoryFilter === "all" ? "border-white bg-white text-black" : "border-white/15 text-zinc-300 hover:border-white/40"}`}
          data-testid="admin-product-filter-all"
        >
          All
        </button>
        {CATEGORIES.map((category) => (
          <button
            key={category.slug}
            type="button"
            onClick={() => setCategoryFilter(category.slug)}
            className={`rounded-full border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] transition ${categoryFilter === category.slug ? "border-cynos-red bg-cynos-red text-white" : "border-white/15 text-zinc-300 hover:border-white/40"}`}
            data-testid={`admin-product-filter-${category.slug}`}
          >
            {category.label}
          </button>
        ))}
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          {filteredProducts.length} products
        </span>
      </div>
      <div className="bg-cynos-card border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 border-b border-white/10">
            <tr>
              <th className="text-left p-4">Image</th>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Category</th>
              <th className="text-left p-4">Colors</th>
              <th className="text-left p-4">Price</th>
              <th className="text-left p-4">Stock</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Bestseller</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((p) => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="p-4"><img src={p.images?.[0]} alt="" className="w-12 h-14 object-cover" /></td>
                <td className="p-4 font-bold uppercase">{p.name}</td>
                <td className="p-4">
                  <div className="flex max-w-[260px] flex-wrap gap-1.5">
                    {getProductCategories(p).map((category) => (
                      <span key={category} className="rounded-full border border-white/10 bg-white/5 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.12em] text-zinc-300">
                        {category}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="p-4 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">{p.colors?.length ? p.colors.join(", ") : "N/A"}</td>
                <td className="p-4 font-anton">{formatINR(p.price)}</td>
                <td className="p-4 font-mono text-xs">{p.stock_count}</td>
                <td className={`p-4 font-mono text-[10px] uppercase tracking-[0.16em] ${Number(p.active_status) === 0 ? "text-zinc-500" : Number(p.active_status) === 2 ? "text-cynos-red" : "text-emerald-300"}`}>
                  {PRODUCT_STATUS_LABELS[Number(p.active_status ?? 1)] || "Active"}
                </td>
                <td className="p-4 font-mono text-xs">{p.is_bestseller ? "YES" : "—"}</td>
                <td className="p-4 flex gap-2">
                  <button onClick={() => setEditing(p)} className="border border-white/20 p-2 hover:border-white" data-testid={`edit-${p.slug}`}><Edit size={14} /></button>
                  <button onClick={() => onDelete(p.id)} className="border border-cynos-red/40 text-cynos-red p-2 hover:border-cynos-red" data-testid={`delete-${p.slug}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filteredProducts.length === 0 && (
              <tr>
                <td colSpan={9} className="p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                  No products in this category
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onSaved(); }}
        />
      )}
    </div>
  );
};

const CategoryMultiSelect = ({ value = [], onChange }) => {
  const [search, setSearch] = useState("");
  const selected = getProductCategories({ categories: value });
  const selectedSlugs = new Set(selected.map(getCategorySlug));
  const options = CATEGORIES.filter((category) =>
    category.label.toLowerCase().includes(search.trim().toLowerCase()) ||
    category.slug.toLowerCase().includes(search.trim().toLowerCase())
  );

  const toggle = (category) => {
    const exists = selectedSlugs.has(category.slug);
    const next = exists
      ? selected.filter((label) => getCategorySlug(label) !== category.slug)
      : [...selected, category.label];
    onChange(next);
  };

  return (
    <div data-testid="prod-categories">
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Categories</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500">Select one or more</span>
      </div>
      <div className="rounded-[10px] border border-white/15 bg-cynos-surface p-3">
        <div className="mb-3 flex min-h-[42px] flex-wrap gap-2 rounded-[8px] border border-white/10 bg-black/30 p-2">
          {selected.length ? selected.map((label) => (
            <button
              key={label}
              type="button"
              onClick={() => onChange(selected.filter((item) => getCategorySlug(item) !== getCategorySlug(label)))}
              className="inline-flex items-center gap-2 rounded-full border border-cynos-red/30 bg-cynos-red/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-cynos-red"
              aria-label={`Remove ${label}`}
            >
              {label} <span aria-hidden="true">×</span>
            </button>
          )) : (
            <span className="px-1 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-600">
              No categories selected
            </span>
          )}
        </div>

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search categories"
          className="mb-3 w-full rounded-[8px] border border-white/10 bg-black/30 px-3 py-3 font-mono text-xs uppercase tracking-[0.16em] text-white outline-none placeholder:text-zinc-600 focus:border-cynos-red"
          data-testid="prod-category-search"
        />

        <div className="grid gap-2 sm:grid-cols-2">
          {options.map((category) => {
            const active = selectedSlugs.has(category.slug);
            return (
              <button
                key={category.slug}
                type="button"
                onClick={() => toggle(category)}
                className={`flex min-h-[44px] items-center gap-3 rounded-[8px] border px-3 text-left transition ${
                  active ? "border-cynos-red bg-cynos-red/10 text-white" : "border-white/10 bg-black/20 text-zinc-300 hover:border-white/30"
                }`}
                data-testid={`prod-category-${category.slug}`}
              >
                <span className={`flex h-4 w-4 items-center justify-center rounded-[4px] border text-[10px] ${active ? "border-cynos-red bg-cynos-red text-white" : "border-white/25"}`}>
                  {active ? "✓" : ""}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.14em]">{category.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ProductDialog = ({ initial, onClose, onSaved }) => {
  const [data, setData] = useState({
    ...initial,
    images: initial.images?.length ? initial.images : [""],
    sizes: initial.sizes?.length ? initial.sizes : ["S", "M", "L", "XL", "XXL"],
    colors: initial.colors || [],
    color_images: initial.color_images || {},
    variant_skus: initial.variant_skus || {},
    badges: initial.badges || [],
    categories: getProductCategories(initial).length ? getProductCategories(initial) : ["Men"],
    category: getCategorySlug(getProductCategories(initial)[0] || initial.category || "men"),
    active_status: initial.active_status ?? 1,
    meta_title: initial.meta_title || "",
    meta_description: initial.meta_description || "",
    meta_keywords: initial.meta_keywords || "",
    seo_slug: initial.seo_slug || "",
  });
  const [sizesText, setSizesText] = useState((initial.sizes?.length ? initial.sizes : ["S", "M", "L", "XL", "XXL"]).join(", "));
  const [colorsText, setColorsText] = useState((initial.colors || []).join(", "));
  const [badgesText, setBadgesText] = useState((initial.badges || []).join(", "));
  const [colorImagesText, setColorImagesText] = useState(() => {
    const source = initial.color_images || {};
    return Object.fromEntries(Object.entries(source).map(([color, urls]) => [color, (urls || []).join("\n")]));
  });
  const [variantSkus, setVariantSkus] = useState(initial.variant_skus || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!initial.id;
  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const selectedColors = splitCsv(colorsText);
  const selectedSizes = splitCsv(sizesText);
  const updateColorImagesText = (color, value) => setColorImagesText((current) => ({ ...current, [color]: value }));
  const updateVariantSku = (color, size, value) => {
    const key = `${color}|${size}`;
    setVariantSkus((current) => ({ ...current, [key]: value }));
  };
  const previewTitle = seoTitlePreview(data);
  const previewDescription = seoDescriptionPreview(data);
  const previewSlug = slugPreview(data.seo_slug || data.name);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const selectedCategories = getProductCategories({ categories: data.categories || [] });
      if (!selectedCategories.length) {
        setError("Select at least one category.");
        setSaving(false);
        return;
      }
      const colorImagesPayload = selectedColors.reduce((acc, colorName) => {
        const urls = splitLines(colorImagesText[colorName] || "");
        if (urls.length) acc[colorName] = urls;
        return acc;
      }, {});
      const payload = {
        name: data.name, description: data.description,
        price: Number(data.price), original_price: Number(data.original_price),
        category: getCategorySlug(selectedCategories[0]),
        categories: selectedCategories,
        images: data.images.filter(Boolean),
        sizes: selectedSizes,
        colors: selectedColors,
        color_images: colorImagesPayload,
        variant_skus: Object.fromEntries(
          Object.entries(variantSkus)
            .map(([key, value]) => [key, String(value || "").trim()])
            .filter(([, value]) => value)
        ),
        badges: splitCsv(badgesText),
        stock_count: Number(data.stock_count),
        is_bestseller: !!data.is_bestseller,
        rating: Number(data.rating || 4.7),
        review_count: Number(data.review_count || 0),
        qikink_sku: data.qikink_sku || "",
        qikinkSku: data.qikinkSku || data.qikink_sku || "",
        qikinkProductId: data.qikinkProductId || "",
        product_sku: data.product_sku || "",
        productSku: data.productSku || data.product_sku || "",
        store_sku: data.store_sku || "",
        storeSku: data.storeSku || data.store_sku || "",
        design_sku: data.design_sku || "",
        designSku: data.designSku || data.design_sku || "",
        variation: data.variation || "",
        color: data.color || "",
        size: data.size || "",
        product_cost: Number(data.product_cost || 0),
        productCost: Number(data.productCost || data.product_cost || 0),
        selling_price: Number(data.selling_price || data.price || 0),
        sellingPrice: Number(data.sellingPrice || data.selling_price || data.price || 0),
        image: data.image || "",
        subCategory: data.subCategory || "",
        variants: data.variants || [],
        qikink_designs: data.qikink_designs || [],
        qikink_variants: data.qikink_variants || [],
        status: data.status || "active",
        active_status: Number(data.active_status ?? 1),
        meta_title: data.meta_title || "",
        meta_description: data.meta_description || "",
        meta_keywords: data.meta_keywords || "",
        seo_slug: data.seo_slug || "",
        isQikinkProduct: !!data.isQikinkProduct,
        source: data.source || "",
      };
      if (isEdit) await adminUpdateProduct(initial.id, payload);
      else await adminCreateProduct(payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save product.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-cynos-card border-white/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-anton text-3xl uppercase">{isEdit ? "Edit" : "New"} Product</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input label="Name" value={data.name} onChange={(v) => update("name", v)} testid="prod-name" />
          <Textarea label="Description" value={data.description} onChange={(v) => update("description", v)} testid="prod-desc" />
          <div className="grid grid-cols-3 gap-3">
            <Input label="Price (₹)" type="number" value={data.price} onChange={(v) => update("price", v)} testid="prod-price" />
            <Input label="Original (₹)" type="number" value={data.original_price} onChange={(v) => update("original_price", v)} testid="prod-orig" />
            <Input label="Stock" type="number" value={data.stock_count} onChange={(v) => update("stock_count", v)} testid="prod-stock" />
          </div>
          <CategoryMultiSelect
            value={data.categories || []}
            onChange={(categories) => update("categories", categories)}
          />
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Storefront Status</span>
            <select value={Number(data.active_status ?? 1)} onChange={(e) => update("active_status", Number(e.target.value))} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3" data-testid="prod-active-status">
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
              <option value={2}>Coming Soon</option>
            </select>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Image URLs (one per line)</span>
            <textarea
              value={data.images.join("\n")}
              onChange={(e) => update("images", e.target.value.split("\n"))}
              rows={3}
              className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 font-mono text-xs"
              data-testid="prod-images"
            />
          </div>
          <Input label="Sizes (comma-separated)" value={sizesText} onChange={setSizesText} testid="prod-sizes" />
          <Input label="Colors (comma-separated)" value={colorsText} onChange={setColorsText} testid="prod-colors" />
          {!!selectedColors.length && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {selectedColors.map((color) => (
                  <span key={color} className="border border-white/15 bg-cynos-surface px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300">
                    {color}
                  </span>
                ))}
              </div>
              <div className="grid gap-3">
                {selectedColors.map((color) => (
                  <label key={`${color}-images`} className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{color} Image URLs (one per line)</span>
                    <textarea
                      value={colorImagesText[color] || ""}
                      onChange={(e) => updateColorImagesText(color, e.target.value)}
                      rows={2}
                      className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 font-mono text-xs outline-none focus:border-white"
                      data-testid={`prod-color-images-${color.toLowerCase().replace(/\s+/g, "-")}`}
                    />
                  </label>
                ))}
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                Empty color image fields use the default product gallery.
              </p>
            </div>
          )}
          <Input label="Badges (comma-separated, e.g. Trending, Limited Stock)" value={badgesText} onChange={setBadgesText} testid="prod-badges" />
          <div className="border-t border-white/10 pt-4 mt-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cynos-red mb-3">SEO Management</div>
            <Input label="SEO Slug" value={data.seo_slug || ""} onChange={(v) => update("seo_slug", v)} testid="prod-seo-slug" />
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              Preview URL: /product/{previewSlug || "auto-generated-slug"}
            </p>
            <SeoInput
              label="Meta Title"
              value={data.meta_title || ""}
              onChange={(v) => update("meta_title", v)}
              max={60}
              ideal="50-60"
              fallback={previewTitle}
              testid="prod-meta-title"
            />
            <SeoTextarea
              label="Meta Description"
              value={data.meta_description || ""}
              onChange={(v) => update("meta_description", v)}
              max={160}
              ideal="140-160"
              fallback={previewDescription}
              testid="prod-meta-description"
            />
            <Input label="Meta Keywords (comma-separated)" value={data.meta_keywords || ""} onChange={(v) => update("meta_keywords", v)} testid="prod-meta-keywords" />
            <div className="mt-4 border border-white/10 bg-black/40 p-4">
              <div className="text-[#8ab4f8] text-base leading-snug">{previewTitle}</div>
              <div className="mt-1 text-xs text-emerald-300">https://cynos.in/product/{previewSlug || "product"}</div>
              <div className="mt-2 text-sm leading-5 text-zinc-300">{previewDescription}</div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-3 mt-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cynos-red mb-2">Qikink Fulfilment</div>
            <Input label="Fallback Qikink SKU (optional)" value={data.qikink_sku || ""} onChange={(v) => update("qikink_sku", v)} testid="prod-qikink-sku" />
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
              Use this only when one SKU applies to every variant. For Qikink color/size SKUs, fill the variant table below.
            </p>
            {!!selectedColors.length && !!selectedSizes.length && (
              <div className="mt-4 border border-white/10">
                <div className="grid grid-cols-[1fr_80px_1.4fr] border-b border-white/10 bg-black/30 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  <span>Color</span>
                  <span>Size</span>
                  <span>SKU</span>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {selectedColors.flatMap((color) => selectedSizes.map((size) => {
                    const key = `${color}|${size}`;
                    return (
                      <div key={key} className="grid grid-cols-[1fr_80px_1.4fr] items-center gap-3 border-b border-white/5 p-3 last:border-b-0">
                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">{color}</span>
                        <span className="font-mono text-xs uppercase">{size}</span>
                        <input
                          value={variantSkus[key] || ""}
                          onChange={(e) => updateVariantSku(color, size, e.target.value)}
                          placeholder={`${color}-${size} SKU`}
                          className="w-full bg-cynos-surface border border-white/15 px-3 py-2 font-mono text-xs outline-none focus:border-white"
                          data-testid={`prod-variant-sku-${color.toLowerCase().replace(/\s+/g, "-")}-${size}`}
                        />
                      </div>
                    );
                  }))}
                </div>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 mt-2">
            <input type="checkbox" checked={!!data.is_bestseller} onChange={(e) => update("is_bestseller", e.target.checked)} className="accent-cynos-red w-4 h-4" data-testid="prod-bestseller" />
            <span className="font-mono text-xs uppercase tracking-[0.2em]">Mark as bestseller</span>
          </label>
          {error && (
            <div className="border border-cynos-red/40 bg-cynos-red/10 p-3 font-mono text-[10px] uppercase tracking-[0.16em] text-red-200">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em]" data-testid="prod-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-cynos-red text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-red-600 disabled:opacity-50" data-testid="prod-save">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Input = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white" data-testid={testid} />
  </label>
);

const Textarea = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white" data-testid={testid} />
  </label>
);

const SeoInput = ({ label, value, onChange, max, ideal, fallback, testid }) => {
  const shown = value || fallback || "";
  const tooLong = shown.length > max;
  return (
    <label className="block">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.16em] ${tooLong ? "text-red-300" : "text-zinc-500"}`}>
          {shown.length}/{max} ideal {ideal}
        </span>
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={fallback}
        className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white"
        data-testid={testid}
      />
      {tooLong && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-red-300">Too long for best search display.</p>}
    </label>
  );
};

const SeoTextarea = ({ label, value, onChange, max, ideal, fallback, testid }) => {
  const shown = value || fallback || "";
  const tooLong = shown.length > max;
  return (
    <label className="block">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.16em] ${tooLong ? "text-red-300" : "text-zinc-500"}`}>
          {shown.length}/{max} ideal {ideal}
        </span>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={fallback}
        rows={3}
        className="mt-1 w-full bg-cynos-surface border border-white/15 px-3 py-3 outline-none focus:border-white"
        data-testid={testid}
      />
      {tooLong && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-red-300">Too long for best search display.</p>}
    </label>
  );
};

export default AdminDashboard;
